import { Link, useLocation } from "@tanstack/react-router";
import { Menu, ShoppingBag, X, BookOpen, User } from "lucide-react";
import { useId, useState } from "react";
import "./site-chrome-refresh.css";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCartStore } from "@/stores/cartStore";
import { Button } from "@/components/ui/button";
import { ResearchDrawer } from "@/components/ResearchDrawer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { InauguralPriceBanner } from "@/components/InauguralPriceBanner";
import { UrgencyStrip } from "@/components/urgency/UrgencyStrip";

import { SocialLinks } from "@/components/SocialLinks";
import { PrivacySettingsLink } from "@/components/consent/PrivacySettingsDialog";
import { getAffiliatePublicConfig } from "@/lib/affiliate/portal.functions";
import truweLogo from "@/assets/opt/truwe-logo-precision.webp";
import { useT } from "@/lib/i18n/provider";





export function SiteHeader({ onCartClick }: { onCartClick: () => void }) {
  const totalItems = useCartStore((s) => s.items.reduce((sum, i) => sum + i.quantity, 0));
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isKids = pathname.startsWith("/kids");

  const links = [
    { to: "/shop", label: "Shop" },
    { to: "/consult", label: "Consult" },
    { to: "/diet-plan", label: "Free diet plan" },
    { to: "/ingredients", label: "Science" },
    { to: "/blog", label: "Journal" },
    { to: "/kids", label: "Kids" },
  ] as const;

  const goals = [
    { slug: "sleep-recovery", label: "Sleep & recovery" },
    { slug: "pcos-nutrition", label: "PCOS & hormonal balance" },
    { slug: "liver-gut-support", label: "Liver & digestion" },
  ] as const;

  return (
    <header
      data-site-header
      data-brand={isKids ? "kids" : "adult"}
      className="site-header sticky top-0 z-40 w-full border-b backdrop-blur"
    >
      {/* Inaugural pricing flash banner */}
      <InauguralPriceBanner />

      {/* Two-tone segmented brand bar */}

      <div className="brand-split">
        <Link to="/" activeOptions={{ exact: true }} className="seg-adult">
          TruWe <span className="hidden opacity-70 sm:inline">for adults</span>
          <span className="opacity-70 sm:hidden">Adults</span>
        </Link>
        <Link to="/kids" className="seg-kids">
          TruWe Kids <span className="hidden opacity-70 sm:inline">for little ones</span>
        </Link>
      </div>

      {/* Sliding announcement bar (with arrows + locale selectors) */}
      <AnnouncementBar />

      {/* Admin-run sale strip — only renders while a campaign is live */}
      <UrgencyStrip />




      {/* Main row: 3-column grid so the centered logo never overlaps nav/CTA */}
      <div className="relative mx-auto grid h-14 max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-2 px-3 md:h-20 md:px-8">
        <div className="flex items-center justify-self-start">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden -ml-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <nav className="hidden items-center gap-6 lg:flex">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-[12px] font-semibold uppercase tracking-[0.14em] text-foreground/75 transition-colors hover:text-brand"
                activeProps={{ className: "text-brand" }}
              >
                {l.label}
              </Link>
            ))}
            <div className="group relative">
              <button
                type="button"
                className="text-[12px] font-semibold uppercase tracking-[0.14em] text-foreground/75 transition-colors hover:text-brand"
                aria-haspopup="menu"
              >
                Shop by goal
              </button>
              <div className="pointer-events-none invisible absolute left-0 top-full z-50 mt-2 w-64 rounded-2xl border border-border bg-background p-2 opacity-0 shadow-luxury-lg transition-opacity group-hover:pointer-events-auto group-hover:visible group-hover:opacity-100 focus-within:pointer-events-auto focus-within:visible focus-within:opacity-100">
                {goals.map((g) => (
                  <Link
                    key={g.slug}
                    to="/collections/$slug"
                    params={{ slug: g.slug }}
                    className="block rounded-xl px-3 py-2 text-sm font-medium text-brand-deep transition-colors hover:bg-brand-soft/60"
                  >
                    {g.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>

        <Link
          to="/"
          onClick={() => setOpen(false)}
          aria-label="TruWe home"
          className="flex flex-col items-center justify-self-center"
        >
          <span
            aria-label="TRUWE"
            className="font-display font-extrabold text-foreground select-none leading-none whitespace-nowrap text-[18px] sm:text-[22px] md:text-[28px] tracking-[0.08em]"
          >
            TRUWE
          </span>
        </Link>

        <div className="flex items-center gap-1 justify-self-end md:gap-3">
          <Link
            to="/shop"
            className="hidden h-10 items-center justify-center rounded-full bg-brand-deep px-6 text-[12px] font-bold uppercase tracking-[0.22em] text-white transition-all hover:-translate-y-0.5 hover:bg-brand-deep/90 lg:inline-flex"
          >
            Shop Now
          </Link>
          <Link
            to="/account"
            aria-label="My account"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-foreground/80 transition-colors hover:bg-muted hover:text-brand"
          >
            <User className="h-5 w-5" />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCartClick}
            className="relative -mr-2 md:mr-0"
            aria-label="Open cart"
          >
            <ShoppingBag className="h-5 w-5" />
            {totalItems > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-primary-foreground">
                {totalItems}
              </span>
            )}
          </Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border/60 bg-background lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col px-4 py-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="py-3 text-base font-semibold uppercase tracking-[0.14em] text-foreground/90"
                activeProps={{ className: "text-brand" }}
              >
                {l.label}
              </Link>
            ))}
            <div className="mt-2 border-t border-border/60 pt-2">
              <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">Shop by goal</p>
              {goals.map((g) => (
                <Link
                  key={g.slug}
                  to="/collections/$slug"
                  params={{ slug: g.slug }}
                  onClick={() => setOpen(false)}
                  className="block py-2.5 text-sm font-medium text-foreground/85"
                >
                  {g.label}
                </Link>
              ))}
            </div>
            <ResearchDrawer>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 py-3 text-base font-semibold uppercase tracking-[0.14em] text-foreground/90"
              >
                <BookOpen className="h-4 w-4" />
                Clinical Library
              </button>
            </ResearchDrawer>
            <Link
              to="/account"
              onClick={() => setOpen(false)}
              className="py-3 text-base font-semibold uppercase tracking-[0.14em] text-foreground/90"
            >
              My account
            </Link>
            <Link
              to="/shop"
              onClick={() => setOpen(false)}
              className="my-3 inline-flex h-12 items-center justify-center rounded-full bg-brand-deep px-6 text-[12px] font-bold uppercase tracking-[0.22em] text-white"
            >
              Shop Now
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  const t = useT();
  const fetchConfig = useServerFn(getAffiliatePublicConfig);
  const cfgQuery = useQuery({
    queryKey: ["affiliate", "public-config"],
    queryFn: () => fetchConfig(),
    staleTime: 5 * 60_000,
    // Fail silently — footer must still render if the RPC errors.
    retry: false,
  });
  const affiliateEnabled = cfgQuery.data?.enabled === true;
  return (
    <footer data-site-footer className="tw-footer">
      <div className="tw-footer__inner">
        <div className="tw-footer__brand">
          <Link to="/" aria-label="TruWe" className="inline-flex items-center">
            <img
              src={truweLogo}
              alt="TruWe"
              width={960}
              height={209}
              className="h-14 w-auto select-none md:h-16"
              draggable={false}
            />
          </Link>
          <p className="mt-2 max-w-xs text-sm text-muted-foreground">
            {t("foot.blurb2")}
          </p>
          <SocialLinks className="mt-5" />
          <Link className="tw-footer__support" to="/contact">{t("foot.link.helpContact")} <span aria-hidden="true">↗</span></Link>
        </div>
        <FooterCol title={t("foot.col.shop")}>
          <FooterLink to="/shop">{t("foot.link.allProducts")}</FooterLink>
          <FooterLink to="/kids">{t("foot.link.truweKids")}</FooterLink>
          <FooterLink to="/quiz">{t("foot.link.findRoutine")}</FooterLink>
          <FooterLink to="/diet-plan">{t("foot.link.dietPlan")}</FooterLink>
          <FooterLink to="/ingredients">{t("foot.link.ingredients")}</FooterLink>
        </FooterCol>
        <FooterCol title={t("foot.col.shopByNeed")}>
          <FooterLink to="/collections/magnesium-supplements">{t("foot.link.magnesium")}</FooterLink>
          <FooterLink to="/collections/sleep-recovery">{t("foot.link.sleepRecovery")}</FooterLink>
          <FooterLink to="/collections/inositol-supplements">{t("foot.link.inositol")}</FooterLink>
          <FooterLink to="/collections/pcos-nutrition">{t("foot.link.pcos")}</FooterLink>
          <FooterLink to="/collections/liver-gut-support">{t("foot.link.liverGut")}</FooterLink>
          <FooterLink to="/collections/kids-nutrition">{t("foot.link.kidsNutrition")}</FooterLink>
        </FooterCol>
        <FooterCol title={t("foot.col.company")}>
          <FooterLink to="/about">{t("foot.link.about")}</FooterLink>
          <FooterLink to="/contact">{t("foot.link.contact")}</FooterLink>
          <FooterLink to="/trucoins">{t("foot.link.loyalty")}</FooterLink>
          {affiliateEnabled && (
            <>
              <FooterLink to="/affiliate/apply">{t("foot.link.becomeAffiliate")}</FooterLink>
              <FooterLink to="/affiliate/login">{t("foot.link.affiliateLogin")}</FooterLink>
            </>
          )}
        </FooterCol>
        <FooterCol title={t("foot.col.support")}>
          <FooterLink to="/contact">{t("foot.link.helpContact")}</FooterLink>
          <FooterLink to="/partner-enquiry">{t("foot.link.bulkEnquiry")}</FooterLink>
          <FooterLink to="/policies/shipping-policy">{t("foot.link.shippingPolicy")}</FooterLink>
          <FooterLink to="/policies/return-exchange-policy">{t("foot.link.returnExchange")}</FooterLink>
          <FooterLink to="/policies/refund-policy">{t("foot.link.refundPolicy")}</FooterLink>
          <FooterLink to="/policies/privacy-policy">{t("foot.link.privacyPolicy")}</FooterLink>
          <FooterLink to="/policies/terms-of-service">{t("foot.link.termsOfService")}</FooterLink>
          <li>
            <PrivacySettingsLink />
          </li>
        </FooterCol>
      </div>
      <div className="tw-footer__legal border-t border-border/60">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-3 px-4 py-6 text-xs text-muted-foreground md:flex-row md:items-center md:px-8">
          <p>{t("foot.copyright", { year: String(new Date().getFullYear()) })}</p>
          <div className="grid w-full grid-cols-2 items-center gap-3 sm:grid-cols-4 md:w-auto md:gap-6">
            <Badge src="/__l5e/assets-v1/abb35464-bc7d-4811-8701-de130c801d80/fssai-logo-mono.png" alt="FSSAI" label="FSSAI Lic. 22625072001267" />
            <Badge src="/__l5e/assets-v1/55fde897-747c-4cbf-bdc5-13c635d35af1/usfda-mono.png" alt="USFDA" label="USFDA registered" />
            <Badge src="/__l5e/assets-v1/b0fd5407-cd6d-4ebb-9b15-577f94847ae9/gmp-mono.png" alt="GMP" label="GMP certified" />
            <Badge src="/__l5e/assets-v1/79781037-78f5-4609-86a8-4c6ebed3f7d5/iso-mono.png" alt="ISO" label="ISO certified" />
          </div>
          <p className="mt-2 text-[11px] text-brand-deep/50">{t("foot.manufactured")}</p>
        </div>
      </div>
    </footer>
  );
}


function FooterCol({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="tw-footer__column">
      <h2 className="tw-footer__desktop-title">{title}</h2>
      <button type="button" className="tw-footer__toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>{title}<span aria-hidden="true">{open ? "−" : "+"}</span></button>
      <ul id={id} className={`tw-footer__links ${open ? "is-open" : ""}`}>{children}</ul>
    </div>
  );
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link to={to} className="text-sm text-muted-foreground transition-colors hover:text-brand">
        {children}
      </Link>
    </li>
  );
}

function Badge({ src, alt, label }: { src: string; alt: string; label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 text-center">
      <img src={src} alt={alt} width={32} height={32} loading="lazy" className="h-8 w-8 opacity-70 grayscale" />
      <span className="text-[10px] font-medium leading-tight text-brand-deep/70">{label}</span>
    </div>
  );
}
